import { concat, isEmpty, get, cloneDeep } from 'lodash';
import CostingType from './Costing.type';
import { Page_Size } from '../../../../../Components/helper';
import type { CostingAction, CostingRecord, CostingTodoListState, SetCostingRequestsTodolistPayload } from './Costing.type';

/* initialState is a deliberate byte-for-byte copy of the JavaScript original. The 19 filter
   keys that SET_FILTER_SORT_DATA merges in at runtime are intentionally ABSENT here: they read
   as undefined today and axios omits them from the query string, so seeding them with []
   would change the request wire format on every call made before a filter is applied. */
const initialState: CostingTodoListState = {
  costingListData: [],
  costingSelectedIds: [],
  costingTodoList: [],
  mainSamples: [],
  costingFilterValues: {
    selectedcosting_Season: [],
    selectedcosting_Year: [],
    selectedcosting_Brand: [],
    selectedcosting_Supplier: [],
    selectedcosting_Type: '',
    selectedcosting_Category1: [],
    selectedcosting_Category2: [],
    selectedcosting_Category3: [],
    selectedcosting_Category4: [],
    selectedcosting_Section: '',
    selectedcosting_Status: [],
    searchText: '',
    pageIndex: 1,
    pageSize: Page_Size,
    sortOrder: '',
    sortPropertyName: '',
    loading: false,
    selectedLicensor: [],
    selectedLicense: [],
    selectedLikeness: [],
  },
  costingListTotalCount: 0,
  supplierBrandsList: [],
  averageTotalCal: null,
  currentRequestId: null,
  costingSaveState: {},
};

/**
 * Decides whether an incoming page replaces the current list or is appended to it.
 * A non-empty searchText combined with isScrolled appends; searchText alone replaces;
 * isScrolled alone appends; otherwise the payload replaces wholesale.
 */
const setArrayByCase = (array: CostingRecord[], payload: SetCostingRequestsTodolistPayload): CostingRecord[] => {
  if (!isEmpty(get(payload, 'searchText')) && get(payload, 'isScrolled')) {
    return [...array, ...payload.data];
  }
  if (!isEmpty(get(payload, 'searchText'))) {
    return payload.data;
  }
  if (get(payload, 'isScrolled')) {
    return concat(array, payload.data);
  }
  return payload.data;
};

/**
 * Costing TODO list slice, mounted in reducers/index.js under the key `CostingTodoListData`.
 * Six of the ten declared action types are handled; the rest are dispatched but ignored.
 */
const costingTodoListReducer = (state: CostingTodoListState = initialState, action: CostingAction): CostingTodoListState => {
  switch (action.type) {
    case CostingType.SET_COSTING_REQUESTS_TODOLIST: {
      if (action.payload.requestId && state.currentRequestId && Number(action.payload.requestId) < Number(state.currentRequestId)) {
        return state;
      }
      const updatedArry = cloneDeep(setArrayByCase(state.costingListData, action.payload));
      return {
        ...state,
        costingListData: updatedArry,
        costingListTotalCount: action.payload.totalCount,
        currentRequestId: action.payload.requestId || state.currentRequestId,
      };
    }
    case CostingType.PATCH_COSTING: {
      /* Replaces one costing in place after an inline edit; the other rows keep their identity so
         the grid only repaints the row that changed. */
      const index = state.costingListData.findIndex((costing) => costing?._id === action.payload.costingId);
      if (index === -1) return state;
      const costingListData = [...state.costingListData];
      costingListData[index] = action.payload.costing;
      return { ...state, costingListData };
    }
    case CostingType.SET_COSTING_SAVE_STATE: {
      const costingSaveState = { ...state.costingSaveState };
      if (action.payload.status === null) {
        delete costingSaveState[action.payload.costingId];
      } else {
        costingSaveState[action.payload.costingId] = action.payload.status;
      }
      return { ...state, costingSaveState };
    }
    case CostingType.SET_SELECTED_IDS_ARRAY:
      return {
        ...state,
        costingSelectedIds: action.payload,
      };
    case CostingType.SET_FILTER_SORT_DATA:
      return {
        ...state,
        costingFilterValues: {
          ...state.costingFilterValues,
          ...action.payload,
        },
      };
    case CostingType.GET_MAIN_SAMPLES:
      return {
        ...state,
        mainSamples: action.payload,
      };

    case CostingType.SUPPLIER_BRAND:
      return {
        ...state,
        supplierBrandsList: action.payload,
      };
    case CostingType.SET_COSTING_STATS:
      return {
        ...state,
        averageTotalCal: action.payload,
      };
    default:
      return state;
  }
};

export default costingTodoListReducer;
